import React, { useMemo, useState } from 'react';
import Dropdown from '../connectedForm/Dropdown';
import {
  IDownloadEcSalesReportMutationResult,
  IDownloadEcSalesReportMutationVariables,
} from '../../graphql/schema/p4types';
import { useMutation } from '@apollo/client';
import { ModalBottomButtonContainer } from './Styles';
import { SecondaryButton, SuccessButton } from '../connectedForm/Button';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import { Modal } from '@fluentui/react';
import { BasicHeader } from '../../actions/BasicHeader';
import { BlurTextField } from '../connectedForm/inputComponents';
import { useCompany } from '../../utils/FetchConfig';
import DownloadEcSalesReport from '../../graphql/mutations/customer/EcSalesReportMutation.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { useActionCriteria } from '../../utils/useActionProps';

export interface IEcSalesModalProps {
  onClose: () => void;
  modalOpen: boolean;
}

const useEuSalesModal = ({ onClose, modalOpen }: IEcSalesModalProps) => {
  const company = useCompany();
  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const initialYear = String(new Date().getFullYear());
  const initialPeriod = String(new Date().getMonth() + 1).padStart(2, '0');
  const [vatNumber, setVatNumber] = useState<string>(company?.vatRegistration || '');
  const [subsidiaryId, setSubsidiaryId] = useState<string>('');
  const [year, setYear] = useState<string>(initialYear);
  const [period, setPeriod] = useState<string>(initialPeriod);
  const handleQueryError = useComponentQueryErrorHandling();
  const title = 'EC Sales Report';
  const clientMutationId = getClientMutationId();

  const actionCriteria = useActionCriteria();

  const inputVariables = {
    input: {
      actionCriteria: normalizeVariables(actionCriteria, ['ecSalesListing']),
      vatNumber: vatNumber.replace(/\s+/g, ''),
      subsidiaryId,
      year,
      period,
      clientMutationId,
    },
  };

  const onClickDownloadButton = () => {
    generateReport().then((result) => {
      runBackgroundProcess(result?.data?.downloadEcSalesReport?.result as IJobMeta);
      onClose();
    });
  };

  const [generateReport] = useMutation<IDownloadEcSalesReportMutationResult, IDownloadEcSalesReportMutationVariables>(
    DownloadEcSalesReport,
    {
      onError: handleQueryError,
      variables: inputVariables,
    }
  );

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Download EC Sales Report',
    onComplete: (job) => {
      checkPopUpBlockerFunction(`/api/resources/temporary/${job.properties.reportFile}`, '_blank');
    },
  });

  const periods = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const period = String(i + 1).padStart(2, '0');
      return { id: period, name: period };
    });
  }, []);

  const years = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const year = String(new Date().getFullYear() - i);
      return { id: year, name: year };
    });
  }, []);

  return (
    <Modal isOpen={modalOpen} styles={{ main: { minWidth: '600px', paddingBottom: '60px' } }}>
      <BasicHeader>{title}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <BlurTextField
          label="VAT Number"
          value={vatNumber}
          onChange={setVatNumber}
          enableOnChange
          data-testid={'vat-number'}
          inputCols={2 / 3}
          labelCols={1 / 3}
        />
        <BlurTextField
          label="Subsidiary Id"
          value={subsidiaryId}
          onChange={setSubsidiaryId}
          enableOnChange
          data-testid={'subsidiary-id'}
          inputCols={2 / 3}
          labelCols={1 / 3}
        />
        <Dropdown
          options={years}
          valueKey="id"
          labelKey="name"
          onChange={(value: string) => setYear(value)}
          label="Year"
          value={year}
          data-testid={'year'}
          inputCols={2 / 3}
          labelCols={1 / 3}
          valueOnly={true}
        />
        <Dropdown
          options={periods}
          valueKey="id"
          labelKey="name"
          onChange={(value: string) => setPeriod(value)}
          label="Period"
          value={period}
          data-testid={'period'}
          inputCols={2 / 3}
          labelCols={1 / 3}
          valueOnly={true}
        />
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onClose}>Skip</SecondaryButton>
        <SuccessButton onClick={onClickDownloadButton}>Download</SuccessButton>
      </ModalBottomButtonContainer>
    </Modal>
  );
};

export default useEuSalesModal;
